import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {Button, Card, CardBody, Col, Form, Modal, Nav, Row, Tab,} from "react-bootstrap";
import Select from "react-select";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,} from "@dnd-kit/sortable";
import {DocumentTemplateService} from "./service";
import RichTextEditor from "./RichTextEditor";
import {CustomerService} from "../customers/service";
import {ErrorToast, SuccessToast} from "../../shared/toasters/toasters";
import {
    DocumentTemplateSectionType,
    IDocumentTemplate,
    IDocumentTemplateCustomerAssignment,
    IDocumentTemplateSection,
    IDocumentTemplateVariable,
} from "../../shared/interfaces/document-template/IDocumentTemplate";
import {instance, requests} from "../../helpers/api.services";
import {BASE_URL} from "../../helpers/api.routes";
import {SortableRow} from "./components/SortableRow";
import {ICustomer} from "../../shared/interfaces/customer/ICustomer";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";


const schema = yup.object().shape({
    name: yup.string().required("validation.required"),
    description: yup.string().optional(),
    titleMask: yup.string().optional(),
    isDefault: yup.boolean().optional(),
    isActive: yup.boolean().optional(),
    settings: yup
        .object()
        .shape({
            companyName: yup.string().optional(),
            companyAddress: yup.string().optional(),
            companyCity: yup.string().optional(),
            companyPhone: yup.string().optional(),
            companyEmail: yup.string().optional(),
            companyWebsite: yup.string().optional(),
            fontFamily: yup.string().optional(),
            primaryColor: yup.string().optional(),
            secondaryColor: yup.string().optional(),
            logoPath: yup.string().optional(),
            paymentTerms: yup.string().optional(),
        })
        .optional(),
});

const sectionTypeOptions: { value: DocumentTemplateSectionType; label: string }[] = [
    { value: "COVER_PAGE", label: "COVER_PAGE" },
    { value: "INTRO", label: "INTRO" },
    { value: "ITEMS_TABLE", label: "ITEMS_TABLE" },
    { value: "TOTALS", label: "TOTALS" },
    { value: "PAYMENT_TERMS", label: "PAYMENT_TERMS" },
    { value: "FOOTER_INFO", label: "FOOTER_INFO" },
];

const fontFamilyOptions = [
    { value: "Arial", label: "Arial" },
    { value: "Times New Roman", label: "Times New Roman" },
    { value: "Helvetica", label: "Helvetica" },
    { value: "Roboto", label: "Roboto" },
];

interface ISectionFormState {
    type: DocumentTemplateSectionType;
    order: number;
    isEnabled: boolean;
    content: string;
}

const defaultSectionState: ISectionFormState = {
    type: "COVER_PAGE",
    order: 1,
    isEnabled: true,
    content: "",
};




const DocumentTemplateForm = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    // Sections state
    const [sections, setSections] = useState<IDocumentTemplateSection[]>([]);
    const [showSectionModal, setShowSectionModal] = useState(false);
    const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
    const [sectionForm, setSectionForm] = useState<ISectionFormState>(defaultSectionState);
    const [variables, setVariables] = useState<IDocumentTemplateVariable[]>([]);

    // Customer assignment state
    const [assignedCustomers, setAssignedCustomers] = useState<IDocumentTemplateCustomerAssignment[]>([]);
    const [customerOptions, setCustomerOptions] = useState<{ value: number; label: string }[]>([]);
    const [selectedCustomerToAssign, setSelectedCustomerToAssign] = useState<{
        value: number;
        label: string;
    } | null>(null);

    // Preview state
    const [previewOfferOptions, setPreviewOfferOptions] = useState<{ value: number; label: string }[]>([]);
    const [selectedPreviewOffer, setSelectedPreviewOffer] = useState<{
        value: number;
        label: string;
    } | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // DnD sensors (hooks must be inside the component)
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isDirty },
    } = useForm<IDocumentTemplate>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            name: "",
            description: "",
            titleMask: "",
            isDefault: false,
            isActive: true,
            settings: {
                companyName: "",
                companyAddress: "",
                companyCity: "",
                companyPhone: "",
                companyEmail: "",
                companyWebsite: "",
                fontFamily: "Arial",
                primaryColor: "#000000",
                secondaryColor: "#ffffff",
                logoPath: "",
                paymentTerms: "",
            },
        },
    });

    const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

    const primaryColorValue = watch("settings.primaryColor") ?? "#000000";
    const secondaryColorValue = watch("settings.secondaryColor") ?? "#ffffff";

    useEffect(() => {
        if (isEdit) {
            DocumentTemplateService.getSingle(+id!).then((res) => {
                reset({
                    name: res.name,
                    description: res.description ?? "",
                    titleMask: res.titleMask ?? "",
                    isDefault: res.isDefault,
                    isActive: res.isActive,
                    settings: {
                        companyName: res.settings?.companyName ?? "",
                        companyAddress: res.settings?.companyAddress ?? "",
                        companyCity: res.settings?.companyCity ?? "",
                        companyPhone: res.settings?.companyPhone ?? "",
                        companyEmail: res.settings?.companyEmail ?? "",
                        companyWebsite: res.settings?.companyWebsite ?? "",
                        fontFamily: res.settings?.fontFamily ?? "Arial",
                        primaryColor: res.settings?.primaryColor ?? "#000000",
                        secondaryColor: res.settings?.secondaryColor ?? "#ffffff",
                        logoPath: res.settings?.logoPath ?? "",
                        paymentTerms: res.settings?.paymentTerms ?? "",
                    },
                });
            }).catch(ErrorToast);

            fetchSections();
            fetchAssignedCustomers();
        }
    }, [id, isEdit]);

   
    // Data fetching
   
    const fetchSections = async () => {
        if (!id) return;
        try {
            const data = await DocumentTemplateService.getSections(+id);
            setSections(Array.isArray(data) ? data : []);
        } catch (error) {
            ErrorToast(error);
        }
    };

    const fetchAssignedCustomers = async () => {
        if (!id) return;
        try {
            const data = await DocumentTemplateService.getAssignedCustomers(+id);
            setAssignedCustomers(Array.isArray(data) ? data : []);
        } catch (error) {
            ErrorToast(error);
        }
    };

    const fetchCustomerOptions = async (inputValue: string = "") => {
        try {
            const response = await CustomerService.get({ page: 1, perPage: 50, term: inputValue } as any);
            setCustomerOptions(
                response.data.map((c: ICustomer) => ({
                    value: c.id,
                    label: c.name + (c.lastName ? ` ${c.lastName}` : ""),
                }))
            );
        } catch (error) {
            ErrorToast(error);
        }
    };

    const fetchVariables = async () => {
        if (variables.length > 0) return;
        try {
            const data = await DocumentTemplateService.getVariables();
            setVariables(Array.isArray(data) ? data : []);
        } catch (error) {
            ErrorToast(error);
        }
    };

    const fetchPreviewOffers = async (term = "") => {
        try {
            const res = await requests.get(`${BASE_URL}admin/offers`, { page: 1, perPage: 50, term });
            setPreviewOfferOptions(
                (res.data ?? []).map((o: any) => ({
                    value: o.id,
                    label: `#${o.id} \u2013 ${o.name ?? o.customer?.name ?? ""}`,
                }))
            );
        } catch (e) {
            ErrorToast(e);
        }
    };

   
    // Form submit
   

    const onSubmit = async (data: IDocumentTemplate) => {
        try {
            if (isEdit) {
                await DocumentTemplateService.update(data, +id!);
                SuccessToast(t("documentTemplate.successUpdate"));
                // Keeps the unsaved changes guard from firing after a successful save
                reset(data);
            } else {
                const result = await DocumentTemplateService.create(data);
                SuccessToast(t("documentTemplate.successCreate"));
                navigate(`/document-templates/edit/${result.id}`);
            }
        } catch (error) {
            ErrorToast(error);
        }
    };

   
    // DnD
   

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = sections.findIndex((s) => s.id === active.id);
        const newIndex = sections.findIndex((s) => s.id === over.id);
        const reordered = arrayMove(sections, oldIndex, newIndex);

        // Optimistic update
        setSections(reordered);

        if (!id) return;
        try {
            await Promise.all(
                reordered.map((section, index) =>
                    DocumentTemplateService.updateSection(+id, section.id, { order: index + 1 })
                )
            );
        } catch (error) {
            ErrorToast(error);
            fetchSections(); // revert on error
        }
    };

   
    // Section modal handlers
   

    const openAddSectionModal = () => {
        setEditingSectionId(null);
        setSectionForm({
            ...defaultSectionState,
            order: sections.length + 1,
        });
        setShowSectionModal(true);
    };

    const openEditSectionModal = (section: IDocumentTemplateSection) => {
        setEditingSectionId(section.id);
        setSectionForm({
            type: section.type,
            order: section.order,
            isEnabled: section.isEnabled,
            content: section.content ?? "",
        });
        setShowSectionModal(true);
    };

    const handleSectionSave = async () => {
        if (!id) return;
        try {
            if (editingSectionId !== null) {
                await DocumentTemplateService.updateSection(+id, editingSectionId, sectionForm);
            } else {
                await DocumentTemplateService.addSection(+id, sectionForm);
            }
            SuccessToast(t("documentTemplate.successSectionSave"));
            setShowSectionModal(false);
            fetchSections();
        } catch (error) {
            ErrorToast(error);
        }
    };

    const handleSectionDelete = async (sectionId: number) => {
        if (!id) return;
        try {
            await DocumentTemplateService.deleteSection(+id, sectionId);
            SuccessToast(t("documentTemplate.successSectionDelete"));
            fetchSections();
        } catch (error) {
            ErrorToast(error);
        }
    };

    const handleToggleEnabled = async (section: IDocumentTemplateSection) => {
        if (!id) return;
        try {
            await DocumentTemplateService.updateSection(+id, section.id, {
                isEnabled: !section.isEnabled,
            });
            setSections((prev) =>
                prev.map((s) => (s.id === section.id ? { ...s, isEnabled: !s.isEnabled } : s))
            );
        } catch (error) {
            ErrorToast(error);
        }
    };

   
    // Customer assignment handlers
   

    const handleAssignCustomer = async () => {
        if (!id || !selectedCustomerToAssign) return;
        try {
            await DocumentTemplateService.assignCustomer(+id, selectedCustomerToAssign.value);
            SuccessToast(t("documentTemplate.successAssign"));
            setSelectedCustomerToAssign(null);
            fetchAssignedCustomers();
        } catch (error) {
            ErrorToast(error);
        }
    };

    const handleUnassignCustomer = async (customerId: number) => {
        if (!id) return;
        try {
            await DocumentTemplateService.unassignCustomer(+id, customerId);
            SuccessToast(t("documentTemplate.successUnassign"));
            fetchAssignedCustomers();
        } catch (error) {
            ErrorToast(error);
        }
    };

   
    // Preview
   

    const handleGeneratePreview = async () => {
        if (!selectedPreviewOffer || !id) return;
        setPreviewLoading(true);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        try {
            const response = await instance.get(
                `${BASE_URL}web/offers/pdf-preview/${selectedPreviewOffer.value}?templateId=${id}`,
                { responseType: "blob" }
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            setPreviewUrl(URL.createObjectURL(blob));
        } catch (e) {
            ErrorToast(e);
        } finally {
            setPreviewLoading(false);
        }
    };

   
    // Render
   

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Tab.Container defaultActiveKey="basicInfo">
                                <Nav variant="tabs" className="mb-3">
                                    <Nav.Item>
                                        <Nav.Link eventKey="basicInfo">
                                            {t("documentTemplate.basicInfo")}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="designSettings">
                                            {t("documentTemplate.designSettings")}
                                        </Nav.Link>
                                    </Nav.Item>
                                    {isEdit && (
                                        <Nav.Item>
                                            <Nav.Link eventKey="sections">
                                                {t("documentTemplate.sections")}
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                    {isEdit && (
                                        <Nav.Item>
                                            <Nav.Link
                                                eventKey="customerAssignment"
                                                onClick={() => {
                                                    fetchAssignedCustomers();
                                                    fetchCustomerOptions();
                                                }}
                                            >
                                                {t("documentTemplate.customerAssignment")}
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                    {isEdit && (
                                        <Nav.Item>
                                            <Nav.Link
                                                eventKey="preview"
                                                onClick={() => fetchPreviewOffers()}
                                            >
                                                <i className="ti ti-eye me-1"></i>
                                                {t("documentTemplate.preview")}
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                </Nav>

                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <Tab.Content>
                                        {/* Tab 1 — Basic Info */}
                                        <Tab.Pane eventKey="basicInfo">
                                            <Row className="g-3">
                                                <Col sm={12}>
                                                    <Form.Label className="required">
                                                        {t("documentTemplate.name")}
                                                    </Form.Label>
                                                    <Controller
                                                        name="name"
                                                        control={control}
                                                        render={({ field, fieldState }) => (
                                                            <>
                                                                <Form.Control
                                                                    type="text"
                                                                    isInvalid={!!fieldState.error}
                                                                    placeholder={t("documentTemplate.enterName")}
                                                                    {...field}
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {t(fieldState.error?.message as string)}
                                                                </Form.Control.Feedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={12}>
                                                    <Form.Label>
                                                        {t("documentTemplate.description")}
                                                    </Form.Label>
                                                    <Controller
                                                        name="description"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={3}
                                                                placeholder={t("documentTemplate.enterDescription")}
                                                                {...field}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={12}>
                                                    <Form.Label>
                                                        {t("documentTemplate.titleMask")}
                                                    </Form.Label>
                                                    <Controller
                                                        name="titleMask"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <>
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder={t("documentTemplate.titleMaskHint")}
                                                                    {...field}
                                                                />
                                                                <Form.Text className="text-muted">
                                                                    {t("documentTemplate.titleMaskHint")}
                                                                </Form.Text>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={6}>
                                                    <Controller
                                                        name="isDefault"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Check
                                                                type="checkbox"
                                                                label={t("documentTemplate.isDefault")}
                                                                checked={!!field.value}
                                                                onChange={(e) => field.onChange(e.target.checked)}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={6}>
                                                    <Controller
                                                        name="isActive"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Check
                                                                type="checkbox"
                                                                label={t("documentTemplate.isActive")}
                                                                checked={!!field.value}
                                                                onChange={(e) => field.onChange(e.target.checked)}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </Tab.Pane>

                                        {/* Tab 2 — Design Settings */}
                                        <Tab.Pane eventKey="designSettings">
                                            <Row className="g-3">
                                                <Col sm={12}>
                                                    <h6 className="mb-3">{t("documentTemplate.companyInfo")}</h6>
                                                </Col>
                                                <Col sm={6}>
                                                    <Form.Label>{t("documentTemplate.companyName")}</Form.Label>
                                                    <Controller
                                                        name="settings.companyName"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="text"
                                                                placeholder={t("documentTemplate.companyName")}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={6}>
                                                    <Form.Label>{t("documentTemplate.companyAddress")}</Form.Label>
                                                    <Controller
                                                        name="settings.companyAddress"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="text"
                                                                placeholder={t("documentTemplate.companyAddress")}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={6}>
                                                    <Form.Label>{t("documentTemplate.companyCity")}</Form.Label>
                                                    <Controller
                                                        name="settings.companyCity"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="text"
                                                                placeholder={t("documentTemplate.companyCity")}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={6}>
                                                    <Form.Label>{t("documentTemplate.companyPhone")}</Form.Label>
                                                    <Controller
                                                        name="settings.companyPhone"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="text"
                                                                placeholder={t("documentTemplate.companyPhone")}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={6}>
                                                    <Form.Label>{t("documentTemplate.companyEmail")}</Form.Label>
                                                    <Controller
                                                        name="settings.companyEmail"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="text"
                                                                placeholder={t("documentTemplate.companyEmail")}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={6}>
                                                    <Form.Label>{t("documentTemplate.companyWebsite")}</Form.Label>
                                                    <Controller
                                                        name="settings.companyWebsite"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="text"
                                                                placeholder={t("documentTemplate.companyWebsite")}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            />
                                                        )}
                                                    />
                                                </Col>

                                                <Col sm={12}>
                                                    <hr />
                                                    <h6 className="mb-3">{t("documentTemplate.styling")}</h6>
                                                </Col>
                                                <Col sm={6}>
                                                    <Form.Label>{t("documentTemplate.fontFamily")}</Form.Label>
                                                    <Controller
                                                        name="settings.fontFamily"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                options={fontFamilyOptions}
                                                                value={
                                                                    fontFamilyOptions.find(
                                                                        (o) => o.value === field.value
                                                                    ) ?? null
                                                                }
                                                                onChange={(selected) =>
                                                                    field.onChange(selected?.value)
                                                                }
                                                                classNamePrefix="react-select"
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={3}>
                                                    <Form.Label>{t("documentTemplate.primaryColor")}</Form.Label>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <Form.Control
                                                            type="color"
                                                            style={{ width: 48, height: 38, padding: 2 }}
                                                            value={primaryColorValue}
                                                            onChange={(e) =>
                                                                setValue("settings.primaryColor", e.target.value)
                                                            }
                                                        />
                                                        <Controller
                                                            name="settings.primaryColor"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder="#000000"
                                                                    {...field}
                                                                    value={field.value ?? ""}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </Col>
                                                <Col sm={3}>
                                                    <Form.Label>{t("documentTemplate.secondaryColor")}</Form.Label>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <Form.Control
                                                            type="color"
                                                            style={{ width: 48, height: 38, padding: 2 }}
                                                            value={secondaryColorValue}
                                                            onChange={(e) =>
                                                                setValue("settings.secondaryColor", e.target.value)
                                                            }
                                                        />
                                                        <Controller
                                                            name="settings.secondaryColor"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder="#ffffff"
                                                                    {...field}
                                                                    value={field.value ?? ""}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </Col>

                                                <Col sm={12}>
                                                    <hr />
                                                    <h6 className="mb-3">{t("documentTemplate.logoSettings")}</h6>
                                                </Col>
                                                <Col sm={12}>
                                                    <Form.Label>{t("documentTemplate.logoPath")}</Form.Label>
                                                    <Controller
                                                        name="settings.logoPath"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                type="text"
                                                                placeholder={t("documentTemplate.logoPath")}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            />
                                                        )}
                                                    />
                                                </Col>

                                                <Col sm={12}>
                                                    <hr />
                                                    <h6 className="mb-3">{t("documentTemplate.paymentTerms")}</h6>
                                                </Col>
                                                <Col sm={12}>
                                                    <Controller
                                                        name="settings.paymentTerms"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={4}
                                                                placeholder={t("documentTemplate.paymentTerms")}
                                                                {...field}
                                                                value={field.value ?? ""}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </Tab.Pane>

                                        {/* Tab 3 — Sections (edit mode only) */}
                                        {isEdit && (
                                            <Tab.Pane eventKey="sections" onEnter={fetchSections}>
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <h6 className="mb-0">{t("documentTemplate.sections")}</h6>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={openAddSectionModal}
                                                    >
                                                        <i className="ti ti-plus me-1"></i>
                                                        {t("documentTemplate.addSection")}
                                                    </Button>
                                                </div>
                                                {sections.length === 0 ? (
                                                    <p className="text-muted">{t("documentTemplate.noSections")}</p>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <DndContext
                                                            sensors={sensors}
                                                            collisionDetection={closestCenter}
                                                            onDragEnd={handleDragEnd}
                                                        >
                                                            <table className="table table-hover align-middle">
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ width: 36 }}></th>
                                                                        <th>{t("documentTemplate.sectionType")}</th>
                                                                        <th>{t("documentTemplate.sectionEnabled")}</th>
                                                                        <th>{t("documentTemplate.sectionContent")}</th>
                                                                        <th className="text-end">
                                                                            {t("general.actions")}
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <SortableContext
                                                                        items={sections.map((s) => s.id)}
                                                                        strategy={verticalListSortingStrategy}
                                                                    >
                                                                        {sections.map((section) => (
                                                                            <SortableRow
                                                                                key={section.id}
                                                                                section={section}
                                                                                onEdit={openEditSectionModal}
                                                                                onDelete={handleSectionDelete}
                                                                                onToggleEnabled={handleToggleEnabled}
                                                                                t={t}
                                                                            />
                                                                        ))}
                                                                    </SortableContext>
                                                                </tbody>
                                                            </table>
                                                        </DndContext>
                                                    </div>
                                                )}
                                            </Tab.Pane>
                                        )}

                                        {/* Tab 4 — Customer Assignment (edit mode only) */}
                                        {isEdit && (
                                            <Tab.Pane eventKey="customerAssignment">
                                                <div className="mb-4">
                                                    <h6 className="mb-3">
                                                        {t("documentTemplate.assignCustomer")}
                                                    </h6>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <div style={{ flex: 1 }}>
                                                            <Select
                                                                options={customerOptions}
                                                                value={selectedCustomerToAssign}
                                                                onChange={(selected) =>
                                                                    setSelectedCustomerToAssign(selected as any)
                                                                }
                                                                onInputChange={(inputValue, { action }) => {
                                                                    if (action === "input-change") {
                                                                        fetchCustomerOptions(inputValue);
                                                                    }
                                                                }}
                                                                placeholder={t("documentTemplate.assignCustomer")}
                                                                isClearable
                                                                classNamePrefix="react-select"
                                                            />
                                                        </div>
                                                        <Button
                                                            variant="primary"
                                                            onClick={handleAssignCustomer}
                                                            disabled={!selectedCustomerToAssign}
                                                        >
                                                            <i className="ti ti-plus me-1"></i>
                                                            {t("documentTemplate.assignCustomer")}
                                                        </Button>
                                                    </div>
                                                </div>

                                                <h6 className="mb-3">
                                                    {t("documentTemplate.assignedCustomers")}
                                                </h6>
                                                {assignedCustomers.length === 0 ? (
                                                    <p className="text-muted">
                                                        {t("documentTemplate.noAssignedCustomers")}
                                                    </p>
                                                ) : (
                                                    <div className="table-responsive">
                                                        <table className="table table-hover">
                                                            <thead>
                                                                <tr>
                                                                    <th>{t("documentTemplate.name")}</th>
                                                                    <th className="text-end">
                                                                        {t("general.actions")}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {assignedCustomers.map((assignment) => (
                                                                    <tr key={assignment.id}>
                                                                        <td>
                                                                            {assignment.customer.name}
                                                                            {assignment.customer.lastName
                                                                                ? ` ${assignment.customer.lastName}`
                                                                                : ""}
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <Button
                                                                                variant="outline-danger"
                                                                                size="sm"
                                                                                onClick={() =>
                                                                                    handleUnassignCustomer(assignment.customer.id)
                                                                                }
                                                                            >
                                                                                <i className="ti ti-user-minus me-1"></i>
                                                                                {t("documentTemplate.unassign")}
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </Tab.Pane>
                                        )}

                                        {/* Tab 5 — Preview (edit mode only) */}
                                        {isEdit && (
                                            <Tab.Pane eventKey="preview">
                                                <Row className="g-3">
                                                    <Col sm={8}>
                                                        <Form.Label>
                                                            {t("documentTemplate.previewSelectOffer")}
                                                        </Form.Label>
                                                        <Select
                                                            options={previewOfferOptions}
                                                            value={selectedPreviewOffer}
                                                            onChange={(v) => setSelectedPreviewOffer(v as any)}
                                                            onInputChange={(val, { action }) => {
                                                                if (action === "input-change") fetchPreviewOffers(val);
                                                            }}
                                                            onMenuOpen={() => fetchPreviewOffers()}
                                                            placeholder={t("documentTemplate.previewSelectOffer")}
                                                            isClearable
                                                            classNamePrefix="react-select"
                                                        />
                                                    </Col>
                                                    <Col sm={4} className="d-flex align-items-end">
                                                        <Button
                                                            variant="primary"
                                                            onClick={handleGeneratePreview}
                                                            disabled={!selectedPreviewOffer || previewLoading}
                                                            className="w-100"
                                                        >
                                                            {previewLoading ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-2" />
                                                                    {t("general.loading") ?? "Loading..."}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="ti ti-file-type-pdf me-2"></i>
                                                                    {t("documentTemplate.generatePreview")}
                                                                </>
                                                            )}
                                                        </Button>
                                                    </Col>
                                                    {previewUrl ? (
                                                        <Col sm={12}>
                                                            <iframe
                                                                src={previewUrl}
                                                                style={{
                                                                    width: "100%",
                                                                    height: "80vh",
                                                                    border: "1px solid #dee2e6",
                                                                    borderRadius: 4,
                                                                }}
                                                                title="PDF Preview"
                                                            />
                                                        </Col>
                                                    ) : (
                                                        <Col sm={12}>
                                                            <div
                                                                className="text-muted text-center py-5"
                                                                style={{
                                                                    border: "2px dashed #dee2e6",
                                                                    borderRadius: 8,
                                                                }}
                                                            >
                                                                <i className="ti ti-file-type-pdf f-48 d-block mb-2"></i>
                                                                {t("documentTemplate.previewHint")}
                                                            </div>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </Tab.Pane>
                                        )}
                                    </Tab.Content>

                                    {/* Always-visible save button */}
                                    <div className="d-flex justify-content-end gap-2 pt-3 border-top mt-3">
                                        <Button type="button" variant="secondary" onClick={() => guardedNavigate("/document-templates")}>
                                            {t("button.cancel")}
                                        </Button>
                                        <Button type="submit" variant="primary">
                                            <i className="ti ti-device-floppy me-1"></i>
                                            {t(`documentTemplate.${isEdit ? "update" : "create"}`)}
                                        </Button>
                                    </div>
                                </Form>
                            </Tab.Container>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Section Modal */}
            <Modal show={showSectionModal} onHide={() => setShowSectionModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingSectionId !== null
                            ? t("documentTemplate.editSection")
                            : t("documentTemplate.addSection")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col sm={6}>
                            <Form.Label>{t("documentTemplate.sectionType")}</Form.Label>
                            <Select
                                options={sectionTypeOptions}
                                value={sectionTypeOptions.find((o) => o.value === sectionForm.type) ?? null}
                                onChange={(selected) =>
                                    setSectionForm((prev) => ({
                                        ...prev,
                                        type: selected?.value ?? "COVER_PAGE",
                                    }))
                                }
                                classNamePrefix="react-select"
                            />
                        </Col>
                        <Col sm={3}>
                            <Form.Label>{t("documentTemplate.sectionOrder")}</Form.Label>
                            <Form.Control
                                type="number"
                                min={1}
                                value={sectionForm.order}
                                onChange={(e) =>
                                    setSectionForm((prev) => ({
                                        ...prev,
                                        order: parseInt(e.target.value) || 1,
                                    }))
                                }
                            />
                        </Col>
                        <Col sm={3} className="d-flex align-items-end pb-2">
                            <Form.Check
                                type="checkbox"
                                label={t("documentTemplate.sectionEnabled")}
                                checked={sectionForm.isEnabled}
                                onChange={(e) =>
                                    setSectionForm((prev) => ({
                                        ...prev,
                                        isEnabled: e.target.checked,
                                    }))
                                }
                            />
                        </Col>
                        <Col sm={12}>
                            <Form.Label>{t("documentTemplate.sectionContent")}</Form.Label>
                            <RichTextEditor
                                value={sectionForm.content}
                                onChange={(html) =>
                                    setSectionForm((prev) => ({ ...prev, content: html }))
                                }
                                variables={variables}
                                onFetchVariables={fetchVariables}
                                placeholder={t("documentTemplate.sectionContentPlaceholder") ?? "Write section content..."}
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSectionModal(false)}>
                        {t("button.close")}
                    </Button>
                    <Button variant="primary" onClick={handleSectionSave}>
                        {t("button.save")}
                    </Button>
                </Modal.Footer>
            </Modal>
            {unsavedChangesModal}
        </React.Fragment>
    );
};

export default DocumentTemplateForm;
